import { QueryClient } from '@tanstack/react-query'
import { network } from '@utils'
import { handleReturn, MutationFunction, PaginatedResponse, QueryFunction } from './root'

export interface IInvitation {
  id: number
  email: string
  permission_group?: string
  permissions?: string[]
  tags: string[]
}

interface InvitationListProps {
  slug: string
}

export const fetchInvitations: QueryFunction<
  InvitationListProps,
  PaginatedResponse<IInvitation>
> = async ({ slug }, params = null) => {
  const url = `/v1/business/${slug}/invitations/`
  const { data } = await network.httpClient.request({
    url,
    method: 'GET',
    params
  })
  return data
}

interface InvitationInstanceProps {
  slug: string
  invitationId: number
}

export const deleteInvitation: MutationFunction<InvitationInstanceProps, void> = async (
  { slug, invitationId },
  params = null
) => {
  const url = `/v1/business/${slug}/invitations/${invitationId}/`
  await network.httpClient.request({
    url,
    method: 'DELETE',
    params
  })
}

export const createInvitation: MutationFunction<InvitationListProps, IInvitation> = async (
  { slug },
  requestData
) => {
  const url = `/v1/business/${slug}/invitations/`
  const { status, data } = await network.httpClient.request({
    url,
    method: 'POST',
    data: requestData,
    validateStatus: () => true
  })
  return handleReturn({ status, data })
}

export type InvitationStatus = 'OPEN' | 'ACCEPTED' | 'REJECTED'

export interface IPublicInvitation {
  email: string
  business_name: string
  business_slug: string
  inviter_name: string
  status: InvitationStatus
  account_exists: boolean
}

interface InvitationTokenProps {
  token: string
}

export const fetchPublicInvitation: QueryFunction<
  InvitationTokenProps,
  IPublicInvitation
> = async ({ token }) => {
  const { status, data } = await network.httpClient.request({
    url: `/v1/invitations/${token}/`,
    method: 'GET',
    validateStatus: () => true
  })
  return handleReturn({ status, data })
}

const respondToInvitation = async (
  token: string,
  action: 'accept' | 'reject'
): Promise<IPublicInvitation> => {
  const { status, data } = await network.httpClient.request({
    url: `/v1/invitations/${token}/${action}/`,
    method: 'POST',
    validateStatus: () => true
  })
  return handleReturn({ status, data })
}

export const acceptInvitationByToken: MutationFunction<InvitationTokenProps, IPublicInvitation> = ({
  token
}) => respondToInvitation(token, 'accept')

export const rejectInvitationByToken: MutationFunction<InvitationTokenProps, IPublicInvitation> = ({
  token
}) => respondToInvitation(token, 'reject')

export const refreshAfterInvitationAccepted = async (queryClient: QueryClient) => {
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: ['user'] }),
    queryClient.invalidateQueries({ queryKey: ['businesses'] })
  ])
  // Overview uses staleTime + keepPreviousData; invalidate alone can leave the
  // pre-accept portfolio on screen. Reset forces an active refetch of the table.
  await queryClient.resetQueries({ queryKey: ['business-overview'] })
}
